<template>
  <header class="z-50 relative" data-behaviour="TopNavigation">
    <SharedCookieInfo
      v-if="main?.element_visibility?.show_cookie_info"
    />
    <div class="bg-white border-b border-neutral-200 py-4 xl:py-4">
      <nav
        :class="[
          'mx-auto px-6',
          { 'container 2xl:max-w-(--breakpoint-xl)': !wide }
        ]"
        :aria-label="t('main_nav_aria_label')"
      >
        <div class="flex justify-between w-full">
          <div class="flex items-center xl:gap-x-10">
            <a :href="baseUrl" :title="t('logo_title', { portal: headerFooterLinks.portal_name })" class="navbar-brand cursor-pointer">
              <UiIcon
                :src="headerFooterLinks.logo"
                width="125"
                height="30"
                alt="Travanto"
                :class="[
                  {'w-[180px] lg:w-[220px] h-[40px] lg:h-[58px] my-[-5px]' : longPortalName },
                  {'w-[125px] lg:w-[192px] h-[32px] lg:h-[42px]' : !longPortalName }
                ]"
              />
            </a>
          </div>
          <div class="hidden xl:flex flex-col xl:flex-row xl:gap-x-5 items-center items-stretch text-left xl:text-center">
            <div class="flex items-center border-b border-neutral-200 xl:border-b-0">
              <UiMaskedLink
                :href="headerFooterLinks.ferienwohnung_vermieten_url"
                :masking-disabled="!headerFooterLinks.ferienwohnung_vermieten_url_masked"
                :new-tab="true"
                tag="span"
                rel="external"
                class="font-semibold text-lg text-neutral-800 block cursor-pointer py-2 px-8 xl:px-0"
              >
                <UiLanguagePlaceholder domain="LayoutHeader" name="rent_out" />
              </UiMaskedLink>
            </div>
            <!-- Profile picture of the logged-in guest: opens the guest area (/guest) -->
            <button
              v-if="guestLoginEnabled && isGuestLoggedIn"
              type="button"
              :title="t('guest_area_title')"
              class="hidden xl:flex items-center cursor-pointer"
              @click="goToGuestArea"
            >
              <img
                v-if="guestPicture"
                :src="guestPicture"
                :alt="t('guest_picture_alt')"
                referrerpolicy="no-referrer"
                class="w-9 h-9 rounded-full object-cover border border-neutral-200"
              >
              <span v-else class="w-9 h-9 rounded-full overflow-hidden border border-neutral-200 inline-block">
                <UiIcon src="/svg/icons/guest-profile-photo.svg" class="w-full h-full" />
              </span>
            </button>
            <div class="relative hidden xl:block">
              <UiCollapsed :outside-click="true" absolute-position="top-7 right-0" :button-title="t('title_burgermenue')">
                <template #buttonInactive>
                  <span class="pt-2.5 pb-2 block">
                    <UiIcon src="/svg/icons/burger-menu.svg" width="18" height="13" class="stroke-neutral-900 inline-block" />
                  </span>
                </template>
                <template #buttonActive>
                  <span class="pt-2.5 pb-2 block">
                    <UiIcon src="/svg/icons/burger-menu.svg" width="18" height="13" class="stroke-neutral-900 inline-block" />
                  </span>
                </template>
                <template #collapseAbsolute>
                  <div class="mx-auto w-[260px] rounded-xl border border-neutral-50 bg-white mt-[15px] drop-shadow-[0_8px_8px_rgba(0,0,0,0.20)] relative">
                    <ul class="text-neutral-800 text-sm text-left pt-4 pb-2 px-2">
                      <li>
                        <a href="/lastminute/" class="flex items-center gap-x-3 font-semibold py-2.5 px-2 w-full rounded-lg hover:bg-neutral-50">
                          <UiIcon src="/svg/icons/percent.svg" width="25" height="22" alt="Prozent Icon" class="stroke-neutral-800 inline-block flex-none" />
                          <UiLanguagePlaceholder domain="LayoutHeader" name="last_minute" />
                        </a>
                      </li>
                      <li>
                        <span class="flex items-start">
                          <UiMaskedLink
                            href="/wishlist"
                            :new-tab="false"
                            tag="button"
                            rel="external"
                            class="font-semibold py-2.5 px-2 w-full rounded-lg hover:bg-neutral-50"
                          >
                            <span class="flex justify-start items-center">
                              <WishlistIconWithCounter :counter-text="true" />
                            </span>
                          </UiMaskedLink>
                        </span>
                      </li>
                      <li
                        v-if="portalDe && (headerFooterLinks.regiondiscovers?.length > 0 || headerFooterLinks.regionbookings?.length > 0 || headerFooterLinks.vacation_links.length > 0)"
                        class="relative border-b xl:border-b-0 border-neutral-200"
                      >
                        <UiCollapsed>
                          <template #buttonActive>
                            <div class="font-semibold flex cursor-pointer items-center py-2.5 px-2 w-full rounded-lg hover:bg-neutral-50">
                              <div class="flex items-center justify-start text-left gap-x-3 font-semibold">
                                <UiIcon src="/svg/icons/beach.svg" width="25" height="22" alt="Portal Icon" class="stroke-neutral-800 inline-block flex-none" />
                                <UiLanguagePlaceholder domain="LayoutHeader" name="vacation" />
                              </div>
                              <UiIcon src="/svg/icons/arrow-thin.svg" width="12" height="12" alt="Travanto" class="fill-neutral-500 -rotate-90 inline-block ml-1.5" />
                            </div>
                          </template>
                          <template #buttonInactive>
                            <div class="font-semibold flex cursor-pointer items-center py-2.5 px-2 w-full rounded-lg hover:bg-neutral-50">
                              <div class="flex items-center justify-start text-left gap-x-3 font-semibold">
                                <UiIcon src="/svg/icons/beach.svg" width="25" height="22" alt="Prozent Icon" class="stroke-neutral-800 inline-block flex-none" />
                                <UiLanguagePlaceholder domain="LayoutHeader" name="vacation" />
                              </div>
                              <UiIcon src="/svg/icons/arrow-thin.svg" width="12" height="12" alt="Travanto" class="fill-neutral-500 rotate-90 inline-block ml-1.5" />
                            </div>
                          </template>
                          <template #collapse>
                            <div class="flex flex-col gap-x-4 gap-y-8 pt-3 pb-3 text-left mt-2 border-t border-neutral-200">
                              <div class="flex w-full">
                                <div class="flex flex-col items-start w-full">
                                  <div v-for="regiondiscover in headerFooterLinks.regiondiscovers" :key="regiondiscover.name" class="text-sm w-full">
                                    <a class="wrap-break-word block w-full py-2 pl-[45px]" :href="regiondiscover.url">
                                      <UiLanguagePlaceholder domain="LayoutHeader" :name="regiondiscover.name" :variables="{ region: headerFooterLinks.location, preposition: headerFooterLinks.preposition }" />
                                    </a>
                                  </div>
                                  <template v-if="headerFooterLinks.vacation_links.length > 0">
                                    <div v-for="vacation_link in headerFooterLinks.vacation_links" :key="vacation_link.name" class="text-sm w-full">
                                      <a class="wrap-break-word block w-full py-2 pl-[45px]" :href="vacation_link.url" :title="t(vacation_link.name + '_title', { region: headerFooterLinks.location, preposition: headerFooterLinks.preposition })">
                                        <UiLanguagePlaceholder domain="LayoutHeader" :name="vacation_link.name" />
                                      </a>
                                    </div>
                                  </template>
                                  <div v-for="regionbooking in headerFooterLinks.regionbookings" :key="regionbooking.name" class="text-sm  w-full">
                                    <a class="wrap-break-word block w-full py-2 pl-[45px]" :href="regionbooking.url">
                                      <UiLanguagePlaceholder domain="LayoutHeader" :name="regionbooking.name" :variables="{ region : headerFooterLinks.location }" />
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </template>
                        </UiCollapsed>
                      </li>
                      <li class="border-t border-neutral-200 mt-2">
                        <UiMaskedLink
                          :href="headerFooterLinks.gastgeber_url"
                          :masking-disabled="!headerFooterLinks.gastgeber_url_masked"
                          :new-tab="true"
                          tag="button"
                          rel="external"
                          :class="['flex items-center gap-x-3 font-semibold mt-2 py-2.5 px-2 w-full rounded-lg hover:bg-neutral-50', {'link-in-button' : !headerFooterLinks.gastgeber_url_masked}]"
                        >
                          <UiIcon src="/svg/icons/house-v2.svg" width="25" height="17" alt="House Icon" class="stroke-neutral-800 inline-block flex-none" />
                          <UiLanguagePlaceholder domain="LayoutHeader" name="host" />
                        </UiMaskedLink>
                      </li>
                    </ul>
                    <div v-if="guestLoginEnabled" class="px-2 pb-4 pt-4 border-t border-neutral-200 mt-2">
                      <!-- Logged in: menu entry styled like the other links, profile picture as icon -->
                      <button
                        v-if="isGuestLoggedIn"
                        type="button"
                        class="flex items-center gap-x-3 font-semibold py-2.5 px-2 w-full rounded-lg hover:bg-neutral-50"
                        @click="goToGuestArea"
                      >
                        <img
                          v-if="guestPicture"
                          :src="guestPicture"
                          :alt="t('guest_picture_alt')"
                          referrerpolicy="no-referrer"
                          class="w-6 h-6 rounded-full object-cover flex-none"
                        >
                        <span v-else class="w-6 h-6 rounded-full overflow-hidden flex-none inline-block">
                          <UiIcon src="/svg/icons/guest-profile-photo.svg" class="w-full h-full" />
                        </span>
                        <UiLanguagePlaceholder domain="LayoutHeader" name="guest_area" />
                      </button>
                      <!-- Logged out -> guest login opens the login modal -->
                      <UiButton v-else color="petrol" buttonstyle="w-full rounded-xl" buttonpadding="py-2.5 px-3" :title="t('guest_login_title')" @click="openLoginModal">
                        <template #icon>
                          <UiIcon src="/svg/icons/person.svg" width="20" height="20" class="fill-white inline-block" />
                        </template>
                        <UiLanguagePlaceholder domain="LayoutHeader" name="guest_login" />
                      </UiButton>
                    </div>
                  </div>
                </template>
              </UiCollapsed>
            </div>
          </div>
          <div class="xl:hidden flex gap-x-5 items-center">
            <UiMaskedLink
              href="/wishlist"
              :new-tab="false"
              tag="button"
              rel="external"
              :aria-label="t('wishlist_aria_label')"
              :masking-aria-label="t('wishlist_aria_label')"
              class="xl:hidden font-semibold text-lg text-neutral-800 block cursor-pointer py-0 px-0"
            >
              <WishlistIconWithCounter />
            </UiMaskedLink>
            <!-- Profile picture of the logged-in guest (left of the burger menu): opens /guest -->
            <button
              v-if="guestLoginEnabled && isGuestLoggedIn"
              type="button"
              :title="t('guest_area_title')"
              class="xl:hidden flex items-center cursor-pointer"
              @click="goToGuestArea"
            >
              <img
                v-if="guestPicture"
                :src="guestPicture"
                :alt="t('guest_picture_alt')"
                referrerpolicy="no-referrer"
                class="w-8 h-8 rounded-full object-cover border border-neutral-200"
              >
              <span v-else class="w-8 h-8 rounded-full overflow-hidden border border-neutral-200 inline-block">
                <UiIcon src="/svg/icons/guest-profile-photo.svg" class="w-full h-full" />
              </span>
            </button>
            <button
              ref="burgerButtonRef"
              type="button"
              class="xl:hidden cursor-pointer inline-block"
              :title="t('title_burgermenue')"
              :aria-label="t('title_burgermenue')"
              :aria-expanded="mobilnav.isOpen"
              aria-controls="mobile-navigation"
              @click="openMobileNav"
            >
              <UiIcon src="/svg/icons/burger-menu.svg" width="18" height="13" class="stroke-neutral-900 inline-block" aria-hidden="true" />
            </button>
          </div>
        </div>
      </nav>
    </div>
    <nav
      v-if="mobilnav.isOpen"
      id="mobile-navigation"
      ref="mobileNavRef"
      class="block xl:hidden fixed bg-white top-0 left-0 right-0 bottom-0 w-full h-full z-50"
      role="dialog"
      aria-modal="true"
      :aria-label="t('mobile_nav_aria_label')"
      @keydown.escape.prevent="closeMobileNav"
    >
      <div class="flex flex-row-reverse border-b border-neutral-200 py-2">
        <button
          ref="mobileNavCloseRef"
          type="button"
          class="py-2 pl-0 pr-3 cursor-pointer"
          :aria-label="t('close_menu_aria_label')"
          @click="closeMobileNav"
        >
          <UiIcon src="/svg/icons/cancel.svg" width="34" height="34" class="fill-neutral-800" aria-hidden="true" />
        </button>
      </div>
      <ul class="flex flex-col xl:flex-row text-left xl:text-center h-full overflow-auto pb-40">
        <li class="border-b border-neutral-200 xl:border-b-0">
          <a
            href="/lastminute/"
            target="_blank"
            rel="external"
            class="flex flex-row items-center gap-x-3 w-full font-semibold text-neutral-800 cursor-pointer py-5 px-8 text-xl xl:py-3 xl:px-5 xl:text-lg"
          >
            <UiIcon src="/svg/icons/percent.svg" width="25" height="22" alt="Prozent Icon" class="stroke-neutral-800 inline-block flex-none" />
            <UiLanguagePlaceholder domain="LayoutHeader" name="last_minute" />
          </a>
        </li>
        <li v-if="portalDe" class="relative border-b xl:border-b-0 border-neutral-200">
          <UiCollapsed>
            <template #buttonActive>
              <div class="py-5 px-8 font-semibold text-xl text-petrol-500 flex cursor-pointer items-center">
                <div class="flex items-center justify-start text-left gap-x-3 font-semibold">
                  <UiIcon src="/svg/icons/beach.svg" width="25" height="22" alt="Portal Icon" class="stroke-neutral-800 inline-block flex-none" />
                  <UiLanguagePlaceholder domain="LayoutHeader" name="vacation" />
                </div>
                <UiIcon src="/svg/icons/arrow-thin.svg" width="12" height="12" alt="Travanto" class="fill-petrol-500 -rotate-90 inline-block ml-1" />
              </div>
            </template>
            <template #buttonInactive>
              <div class="py-5 px-8 font-semibold text-xl text-neutral-800 flex cursor-pointer items-center">
                <div class="flex items-center justify-start text-left gap-x-3 font-semibold">
                  <UiIcon src="/svg/icons/beach.svg" width="25" height="22" alt="Portal Icon" class="stroke-neutral-800 inline-block flex-none" />
                  <UiLanguagePlaceholder domain="LayoutHeader" name="vacation" />
                </div>
                <UiIcon src="/svg/icons/arrow-thin.svg" width="12" height="12" alt="Travanto" class="fill-neutral-800 rotate-90  inline-block ml-1" />
              </div>
            </template>
            <template #collapse>
              <div class="flex flex-col gap-x-4 gap-y-10 pb-8 text-left px-8">
                <div class="flex flex-col items-start gap-y-5">
                  <div v-for="regiondiscover in headerFooterLinks.regiondiscovers" :key="regiondiscover.name" class="text-sm pl-[37px]">
                    <a class="wrap-break-word" :href="regiondiscover.url">
                      <UiLanguagePlaceholder domain="LayoutHeader" :name="regiondiscover.name" :variables="{ region: headerFooterLinks.location, preposition: headerFooterLinks.preposition }" />
                    </a>
                  </div>
                  <template v-if="headerFooterLinks.vacation_links.length > 0">
                    <div v-for="vacation_link in headerFooterLinks.vacation_links" :key="vacation_link.name" class="text-sm pl-[37px]">
                      <a :href="vacation_link.url" :title="t(vacation_link.name + '_title', { region: headerFooterLinks.location, preposition: headerFooterLinks.preposition })">
                        <UiLanguagePlaceholder domain="LayoutHeader" :name="vacation_link.name" />
                      </a>
                    </div>
                  </template>
                  <div v-for="regionbooking in headerFooterLinks.regionbookings" :key="regionbooking.name" class="text-sm pl-[37px]">
                    <a class="wrap-break-word" :href="regionbooking.url">
                      <UiLanguagePlaceholder domain="LayoutHeader" :name="regionbooking.name" :variables="{ region : headerFooterLinks.location }" />
                    </a>
                  </div>
                </div>
              </div>
            </template>
          </UiCollapsed>
        </li>
        <li class="border-b border-neutral-200 xl:border-b-0 text-xl xl:text-base">
          <a
            :href="headerFooterLinks.ferienwohnung_vermieten_url"
            target="_blank"
            rel="external"
            class="flex flex-row items-center gap-x-3 w-full font-semibold text-neutral-800 cursor-pointer py-5 px-8 text-xl xl:py-3 xl:px-5 xl:text-lg"
          >
            <UiIcon src="/svg/icons/house-wave.svg" width="25" height="22" alt="Prozent Icon" class="stroke-neutral-800 inline-block flex-none" />
            <UiLanguagePlaceholder domain="LayoutHeader" name="rent_out" />
          </a>
        </li>
        <li class="py-5 px-8 xl:py-0 xl:px-0 text-xl xl:text-base">
          <UiMaskedLink
            :href="headerFooterLinks.gastgeber_url"
            :masking-disabled="!headerFooterLinks.gastgeber_url_masked"
            :new-tab="true"
            tag="button"
            rel="external"
            class="flex items-center gap-x-3 font-semibold w-full"
          >
            <UiIcon src="/svg/icons/house-v2.svg" width="27" height="18" alt="House Icon" class="stroke-neutral-800 inline-block flex-none" />
            <UiLanguagePlaceholder domain="LayoutHeader" name="host" />
          </UiMaskedLink>
        </li>
        <!-- Logged in: account entry styled like the other menu items (profile picture as icon) -->
        <li v-if="guestLoginEnabled && isGuestLoggedIn" class="py-5 px-8 xl:py-0 xl:px-0 text-xl xl:text-base">
          <button
            type="button"
            class="flex items-center gap-x-3 font-semibold w-full"
            @click="goToGuestArea"
          >
            <img
              v-if="guestPicture"
              :src="guestPicture"
              :alt="t('guest_picture_alt')"
              referrerpolicy="no-referrer"
              class="w-7 h-7 rounded-full object-cover flex-none"
            >
            <span v-else class="w-7 h-7 rounded-full overflow-hidden flex-none inline-block">
              <UiIcon src="/svg/icons/guest-profile-photo.svg" class="w-full h-full" />
            </span>
            <UiLanguagePlaceholder domain="LayoutHeader" name="guest_area" />
          </button>
        </li>
        <!-- Logged out -> guest login opens the login modal -->
        <li v-else-if="guestLoginEnabled" class="py-5 px-8 xl:py-0 xl:px-0">
          <UiButton color="petrol" buttonstyle="w-full rounded-xl" buttonpadding="py-3 px-4" @click="openLoginModal">
            <template #icon>
              <UiIcon src="/svg/icons/person.svg" width="22" height="22" class="fill-white inline-block" />
            </template>
            <UiLanguagePlaceholder domain="LayoutHeader" name="guest_login" />
          </UiButton>
        </li>
      </ul>
    </nav>

    <!-- Guest login modal (email/password + Google) -->
    <GuestLoginModal v-if="guestLoginEnabled" :open="showLoginModal" @hidden="closeLoginModal()" />

    <!-- TR-40866: newsletter opt-in card (One-Tap look). Lazy: the chunk only
         loads for logged-in guests that still have the prompt pending. -->
    <LazyGuestNewsletterToast
      v-if="guestLoginEnabled && isGuestLoggedIn && guestUser?.newsletter_prompt_pending"
    />
  </header>
</template>

<script setup lang="ts">
defineProps({
  wide: {
    type: Boolean,
    default: false
  }
})

const t = useTranslations('LayoutHeader').translate

const { links: headerFooterLinks, main, portalsWithLongLogos } = usePortalData()

const longPortalName = ref(false)

const mobilnav = reactive({
  isOpen: false
})

const burgerButtonRef = ref<HTMLElement | null>(null)
const mobileNavRef = ref<HTMLElement | null>(null)
const mobileNavCloseRef = ref<HTMLElement | null>(null)
const focusTrap = useFocusTrap(mobileNavRef)

const openMobileNav = (): void => {
  mobilnav.isOpen = true
  nextTick(() => {
    focusTrap.activate({ initialFocus: mobileNavCloseRef.value })
    document.body.classList.add('mobile-nav-open')
  })
}

const closeMobileNav = (): void => {
  if (!mobilnav.isOpen) {
    return
  }
  focusTrap.deactivate()
  mobilnav.isOpen = false
  document.body.classList.remove('mobile-nav-open')
}

// Guest login: feature-gated per portal via element_visibility.show_guest_login
// (disabled on non-German portals and Harz/Sauerland). The modal open state is
// global (useGuestLoginModal) so the login prompt on /guest can open it too.
const guestLoginEnabled = useGuestLoginEnabled()
const { isOpen: showLoginModal, open: openGuestLoginModal, close: closeLoginModal } = useGuestLoginModal()
const openLoginModal = () => {
  closeMobileNav()
  openGuestLoginModal()
}

// Logged-in guest: profile picture in header (links to /guest).
// Session is checked client-side (Backend calls are …FromClient). If already
// loaded (e.g. via guest-only middleware), no additional call is made.
const { user: guestUser, isLoggedIn: isGuestLoggedIn } = useGuest()
const guestPicture = computed(() => guestUser.value?.google_auth?.picture || '')
const goToGuestArea = () => {
  closeMobileNav()
  navigateTo('/guest')
}

const { currentPortal } = usePortals()
const portalDe = currentPortal.value.portal.lang === 'de'
const baseUrl = currentPortal.value.portal.home.replace('http://', 'https://')

if (portalsWithLongLogos.includes(headerFooterLinks.portal_domain_name)) {
  longPortalName.value = true
}

onBeforeUnmount(() => {
  document.body.classList.remove('mobile-nav-open')
  focusTrap.deactivate()
})
</script>

<style scoped>
@reference "~/assets/css/main.css";
.arrow-up:before {
  @apply rotate-225 top-[-15px] absolute left-[70px] border-solid border-15 border-t-transparent border-l-transparent border-b-white border-r-white shadow-[3px_3px_6px_rgba(0,0,0,0.05)] ;
  content:"";
}
:deep(.link-in-button a){
  @apply flex flex-row gap-x-3 items-center;
}
</style>
